import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface SubmitDocumentFormProps {
  type: "issue" | "idea";
  buttonLabel: string;
  titlePlaceholder: string;
  bodyPlaceholder: string;
  accentColor: "red" | "amber";
  onSubmit: (data: { title: string; body?: string; priority?: string }) => Promise<unknown>;
  onSuccess?: () => void;
}

const ACCENT_STYLES = {
  red: {
    button: "bg-red-600 hover:bg-red-500 focus:ring-red-500/40",
    border: "border-red-500/30 focus-within:border-red-500/60",
    trigger: "border-red-500/30 text-red-200 hover:bg-red-500/10 hover:border-red-500/50",
    success: "text-red-300",
  },
  amber: {
    button: "bg-amber-600 hover:bg-amber-500 focus:ring-amber-500/40",
    border: "border-amber-500/30 focus-within:border-amber-500/60",
    trigger: "border-amber-500/30 text-amber-200 hover:bg-amber-500/10 hover:border-amber-500/50",
    success: "text-amber-300",
  },
};

export function SubmitDocumentForm({
  type,
  buttonLabel,
  titlePlaceholder,
  bodyPlaceholder,
  accentColor,
  onSubmit,
  onSuccess,
}: SubmitDocumentFormProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState<"low" | "medium" | "high">("medium");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const titleRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const styles = ACCENT_STYLES[accentColor];

  const reset = useCallback(() => {
    setTitle("");
    setBody("");
    setPriority("medium");
    setError(null);
    setSuccess(false);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    reset();
  }, [reset]);

  // Focus title input when form opens
  useEffect(() => {
    if (isOpen && titleRef.current) {
      titleRef.current.focus();
    }
  }, [isOpen]);

  // Escape key to close
  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        close();
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, close]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmedTitle = title.trim();
    if (!trimmedTitle) return;

    setSubmitting(true);
    setError(null);

    try {
      await onSubmit({ title: trimmedTitle, body: body.trim() || undefined, priority });
      setSuccess(true);
      setTimeout(() => {
        close();
        onSuccess?.();
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : `Failed to create ${type}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mb-8">
      {/* Collapsed trigger button */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? "max-h-0 opacity-0" : "max-h-16 opacity-100"
        }`}
      >
        <button
          onClick={() => setIsOpen(true)}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${styles.trigger}`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {buttonLabel}
        </button>
      </div>

      {/* Expanded form */}
      <div
        ref={formRef}
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <form
          onSubmit={handleSubmit}
          className={`bg-dark-card border ${styles.border} rounded-lg p-5 transition-colors`}
        >
          {/* Header row with close button */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-warm-200">{buttonLabel}</h3>
            <button
              type="button"
              onClick={close}
              className="text-warm-500 hover:text-warm-300 transition-colors p-1"
              aria-label="Close form"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Title field */}
          <input
            ref={titleRef}
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={titlePlaceholder}
            required
            disabled={submitting || success}
            className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-md text-warm-100 placeholder-warm-600 text-sm focus:outline-none focus:border-warm-500 transition-colors disabled:opacity-50 mb-3"
          />

          {/* Priority selector */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs text-warm-500 mr-1">{t('common.priority')}</span>
            <div className="inline-flex rounded-md overflow-hidden border border-dark-border">
              <button
                type="button"
                disabled={submitting || success}
                onClick={() => setPriority("low")}
                className={`px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                  priority === "low"
                    ? "bg-slate-600 text-slate-100 border-slate-500"
                    : "bg-transparent text-slate-400 hover:bg-slate-700/40"
                }`}
              >
                Low
              </button>
              <button
                type="button"
                disabled={submitting || success}
                onClick={() => setPriority("medium")}
                className={`px-3 py-1 text-xs font-medium transition-colors border-x border-dark-border disabled:opacity-50 ${
                  priority === "medium"
                    ? "bg-amber-600 text-amber-100 border-amber-500"
                    : "bg-transparent text-amber-400/60 hover:bg-amber-700/20"
                }`}
              >
                Medium
              </button>
              <button
                type="button"
                disabled={submitting || success}
                onClick={() => setPriority("high")}
                className={`px-3 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                  priority === "high"
                    ? "bg-rose-600 text-rose-100 border-rose-500"
                    : "bg-transparent text-rose-400/60 hover:bg-rose-700/20"
                }`}
              >
                High
              </button>
            </div>
          </div>

          {/* Body field */}
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={bodyPlaceholder}
            rows={4}
            disabled={submitting || success}
            className="w-full px-3 py-2 bg-dark-bg border border-dark-border rounded-md text-warm-100 placeholder-warm-600 text-sm focus:outline-none focus:border-warm-500 transition-colors resize-y disabled:opacity-50 mb-4"
          />

          {/* Error message */}
          {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

          {/* Success message */}
          {success && <p className={`${styles.success} text-sm mb-3`}>{t('common.createdSuccessfully')}</p>}

          {/* Submit button */}
          <button
            type="submit"
            disabled={submitting || success || !title.trim()}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-dark-bg disabled:opacity-50 disabled:cursor-not-allowed ${styles.button}`}
          >
            {submitting ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Submitting...
              </>
            ) : (
              buttonLabel
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
